"use client";

import Translations from "@translate/components/EmailLogin/shared/EmailInputField";
import React, { useEffect, useMemo, useRef } from "react";
import { InputField } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { I18nUtil } from "@utils/i18n";
import { isValidEmail } from "../emailValidation";
import type { EmailLoginVariant } from "../types";

const I18n = I18nUtil.createScoped(Translations);

const CONTAINER_CLASS: Record<EmailLoginVariant, string> = {
  desktop:
    "h-11 px-3 rounded-2 bg-background_white_overlay5 border border-border_white_transparent5 flex items-center",
  popup:
    "h-11 px-3 rounded-2 bg-[#043b420d] border border-border_white_transparent5 flex items-center",
  mobile:
    "flex min-h-12 w-full items-center rounded-[10px] border border-solid border-border_solid bg-background_solid_neutral_primary px-3 py-[14px]",
};

interface EmailInputFieldProps {
  variant: EmailLoginVariant;
  email: string;
  onEmailChange: (value: string) => void;
  emailFormatError: boolean;
  canSubmit: boolean;
  placeholder?: string;
}

export function EmailInputField({
  variant,
  email,
  onEmailChange,
  emailFormatError,
  canSubmit,
  placeholder,
}: EmailInputFieldProps) {
  const inputRef = useRef<React.ComponentRef<typeof InputField>>(null);
  const showErrorMsg = !canSubmit && emailFormatError;

  useEffect(() => {
    inputRef.current?.setError?.(emailFormatError);
  }, [emailFormatError]);

  return (
    <div
      className={cn(
        "w-full",
        showErrorMsg && "flex min-h-[68px] flex-col gap-2",
      )}
    >
      <InputField
        ref={inputRef}
        type="email"
        inputMode="email"
        autoComplete="email"
        placeholder={placeholder ?? I18n.t("Please enter the email address")}
        value={email}
        onChange={(e) => onEmailChange(e.target.value)}
        size="small"
        containerClassName={CONTAINER_CLASS[variant]}
        inputClassName="min-w-0 flex-1 border-0 bg-transparent outline-none body_m text-content_primary placeholder:text-content_quaternary"
        aria-invalid={emailFormatError}
        aria-describedby={emailFormatError ? "email-error" : undefined}
      />
      {showErrorMsg && (
        <p
          id="email-error"
          className="body_s shrink-0 text-content_accent2_primary"
          role="alert"
        >
          {I18n.t("Please enter the correct email")}
        </p>
      )}
    </div>
  );
}

export function useEmailInputCanSubmit(
  email: string,
  agreed: boolean,
  sending: boolean,
): boolean {
  return useMemo(() => {
    const trimmed = email.trim();
    return trimmed.length > 0 && isValidEmail(trimmed) && agreed && !sending;
  }, [email, agreed, sending]);
}
